import {downloadBlobAsFile, generateFilename} from 'core/debug-util/utils';


/**
 * Captures a screenshot of the current browser tab using the native Screen Capture API.
 * NOTE: This API requires explicit user permission *every time* it is called.
 * @returns {Promise<void>}
 */
export async function captureAndDownloadScreenshot(): Promise<void> {
    const screenshotBlob = await captureScreenshot();
    const filename = generateFilename('screenshot', 'png');
    downloadBlobAsFile(screenshotBlob, filename);
}

async function captureScreenshot(): Promise<Blob> {
    let mediaStream: MediaStream;
    try {
        // Create stream from current tab
        mediaStream = await navigator.mediaDevices.getDisplayMedia({
            video: true,
            // @ts-ignore new feature
            preferCurrentTab: true,
        });

        // Create an in-memory video element to play the stream
        const video = document.createElement('video');
        video.srcObject = mediaStream;

        const {resolve: resolveOnloadedmetadata, promise: onloadedmetadataPromise} = Promise.withResolvers();
        video.onloadedmetadata = resolveOnloadedmetadata;
        await onloadedmetadataPromise;
        await video.play();

        return drawScreenshotFromVideo(video);
    } catch (err: any) {
        // eslint-disable-next-line no-console
        console.error('Could not capture screenshot. The user may have denied permission.', err, err.name, err.message, err.stack, err.cause);
        throw err;
    } finally {
        // Stop the stream tracks to end the screen capture session
        mediaStream?.getTracks()?.forEach((track) => track.stop());
    }
}

async function drawScreenshotFromVideo(video: HTMLVideoElement) {
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');

    if (context) {
        // Draw the current video frame to the canvas
        context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);

        return canvasToBlob(canvas);
    } else {
        throw new Error('Could not get canvas context.');
    }
}

function canvasToBlob(canvas: HTMLCanvasElement) {
    return new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) {
                resolve(blob);
            } else {
                reject(new Error('Screenshot blob doesnt exist'))
            }
        }, 'image/png');
    });
}